import React from "react";
import Image from "next/image";
import Link from "next/link";
import { GetStaticProps } from "next";
// components
import CustomHead from "src/components/ui/CustomHead";
import Layout from "src/layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import useAllPage from "src/hooks/dynamic/allPage";
// constants
import { EVariants } from "src/constants/variants";
import {
  CheckCircle,
  MessageSquare,
  CreditCard,
  Video,
  Bell,
  FileText,
  Users,
  Award,
  Palette,
  Truck,
} from "lucide-react";

// All features in one array
const allFeatures = [
  {
    title: "Appointment Booking Widget🏷",
    description:
      "Allow clients to book appointments 24/7 directly through your website or app, reducing administrative workload and improving client satisfaction.",
    icon: <MessageSquare className="h-8 w-8 text-primary" />,
    image: "/images/features/dashboard.webp",
    imageLink: "https://youtu.be/MBREyjdNBcA?si=A-JbdmtcbVYTtGSe",
    benefits: [
      "24/7 online appointment scheduling for clients",
      "Automatic calendar integration with your practice",
      "Reduce phone calls and administrative burden",
      "Instant confirmation and reminder notifications",
    ],
    link: "/demo",
  },
  {
    title: "CoPilot✨",
    description:
      "The world's first AI-powered assistant designed specifically for veterinary practices, providing intelligent guidance and support throughout client interactions.",
    icon: <Video className="h-8 w-8 text-primary" />,
    image: "/images/features/copilot-demo.png",
    imageLink: "https://youtu.be/6RiBUtQ1c6U?si=qK_KKmN2HKy7u-CV",
    benefits: [
      "First-of-its-kind AI technology built for veterinarians",
      "Intuitive conversation guidance and suggestions",
      "Smart triage recommendations based on symptoms",
      "Seamless integration with your existing workflow",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
  {
    title: "Wellness Plans🩺",
    description: "Give pet owners a dedicated space to manage their pets' healthcare needs.",
    icon: <Users className="h-8 w-8 text-primary" />,
    image: "/images/features/wellness-plans-mobile.png",
    imageLink: "https://youtu.be/zZSDB6jpiSw?si=e-Ko6MD0dvQMpgjw",
    benefits: [
      "Self-service appointment booking",
      "Access to pet health records",
      "Medication reminders",
      "Secure messaging with your practice",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
  {
    title: "Reminders 🗓",
    description: "Keep pet owners informed about upcoming appointments and medication schedules.",
    icon: <Bell className="h-8 w-8 text-primary" />,
    image: "/images/features/reminders.png",
    imageLink: "https://youtu.be/gNdzDvcr5eY?si=iGKc88GdxpSQL6nU",
    benefits: [
      "Reduce no-shows with automated appointment reminders",
      "Improve medication compliance with timely alerts",
      "Send vaccination due notices automatically",
      "Customizable reminder templates and timing",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
  {
    title: "Loyalty Plans 🏅",
    description: "Reward your clients for their loyalty and encourage repeat visits.",
    icon: <Award className="h-8 w-8 text-primary" />,
    image: "/images/features/loyalty-plans-new.png",
    imageLink: "https://youtu.be/1RKTURvu76w?si=aWFjEzN0NEhWqEhs",
    benefits: [
      "Create customizable loyalty programs",
      "Track client engagement and rewards",
      "Increase client retention and visit frequency",
      "Generate additional revenue through repeat business",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
  {
    title: "CRM 👩🏽‍💻",
    description: "Manage client relationships effectively with our comprehensive CRM tools.",
    icon: <Users className="h-8 w-8 text-primary" />,
    image: "/images/features/crm-dashboard.png",
    imageLink: "https://youtu.be/CwjKt9hQTnU?si=el0PUJz8JQ1t6H9_",
    benefits: [
      "Centralized client information and communication history",
      "Track client preferences and pet details",
      "Segment clients for targeted marketing campaigns",
      "Improve client satisfaction through personalized service",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
  {
    title: "2 Way Chat💬",
    description: "Send targeted SMS campaigns to promote services and keep clients engaged with your practice.",
    icon: <MessageSquare className="h-8 w-8 text-primary" />,
    image: "/images/features/allplatforms.png",
    imageLink: "https://youtu.be/eThs4iqFzS4?si=hvtyvAJx-ubScwx4",
    benefits: [
      "Targeted promotional campaigns",
      "Automated follow-up messages",
      "High open and response rates",
      "GDPR compliant messaging system",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
  {
    title: "WhatsApp📲",
    description: "Connect with clients through WhatsApp for convenient and familiar communication.",
    icon: <MessageSquare className="h-8 w-8 text-primary" />,
    image: "/images/features/whatsapp.png",
    imageLink: "https://youtu.be/9DX8ltqYi5c?si=wDWBab783Wp17WaW",
    benefits: [
      "Reach clients on their preferred messaging platform",
      "Send appointment reminders via WhatsApp",
      "Share photos and documents easily",
      "Automated WhatsApp business messaging",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
  {
    title: "Scribe 📝",
    description:
      "AI-powered note-taking assistant that transforms veterinary consultations into comprehensive records.",
    icon: <FileText className="h-8 w-8 text-primary" />,
    image: "/images/features/scribe.png",
    imageLink: "https://youtu.be/CXfx5wze85A?si=yOBB5x_EojEWXirD",
    benefits: [
      "Automated consultation note generation",
      "Voice-to-text transcription during appointments",
      "Structured medical record formatting",
      "Integration with practice management systems",
    ],
    link: "https://youtu.be/CXfx5wze85A?si=yOBB5x_EojEWXirD",
  },
  {
    title: "Cobranding 🎨",
    description: "Customize the PetsApp interface with your practice's branding and colors.",
    icon: <Palette className="h-8 w-8 text-primary" />,
    image: "/images/features/Screenshot 2025-04-14 at 5.00.01 PM.png",
    imageLink: "https://youtu.be/PrPV048jKwY?si=iOw4d-xp4z6qrIcl",
    benefits: [
      "Custom logo and color scheme integration",
      "Branded client-facing interfaces",
      "Consistent practice identity across all touchpoints",
      "Professional appearance that builds trust",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
  {
    title: "Home Delivery 🚚 (UK only)",
    description: "Provide convenient prescription and product delivery directly to clients' homes.",
    icon: <Truck className="h-8 w-8 text-primary" />,
    image: "/images/features/home-delivery-partners.webp",
    imageLink: "https://youtu.be/lvSMvVyMqK0?si=cP5w239BCnFTJpQ-",
    benefits: [
      "Convenient home delivery service",
      "Prescription medication delivery",
      "Pet food and product delivery",
      "Improved client satisfaction and compliance",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
  {
    title: "Digital Payments💳",
    description: "Accept payments securely through the app, improving cash flow and client convenience.",
    icon: <CreditCard className="h-8 w-8 text-primary" />,
    image: "/images/features/allplatforms.png",
    imageLink: "https://youtu.be/6FfXqY_EHTU?si=MJG9p48tEsNoiyRf",
    benefits: [
      "Secure payment processing",
      "Payment request links via SMS or email",
      "Automated receipts and invoicing",
      "Comprehensive payment tracking",
    ],
    link: "https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab",
  },
];

// Success metrics
const successMetrics = [
  {
    metric: "85%",
    description: "Reduction in missed calls",
  },
  {
    metric: "3+ hours",
    description: "Saved daily per practice",
  },
  {
    metric: "20%",
    description: "Increase in revenue",
  },
  {
    metric: "98%",
    description: "Client satisfaction rate",
  },
];

export default function FeaturesPage() {
  return (
    <>
      <CustomHead
        title="Features | PetsApp"
        description="Discover the powerful features of PetsApp that help veterinary practices streamline their operations and improve client communication."
      />
      <Layout headerProps={{ variant: EVariants.PRIMARY }}>
        <div className="min-h-screen">
          {/* Hero Section */}
          <section className="bg-primary py-16 md:py-24 relative overflow-hidden">
            {/* Top fade gradient */}
            <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[#4285F4] to-primary pointer-events-none z-0"></div>
            <div className="container mx-auto px-4 md:px-6 relative z-10">
              <div className="max-w-3xl mx-auto text-center">
                <Badge className="bg-secondary text-tertiary hover:bg-secondary/80 mb-4 mx-auto">
                  All-in-one solution
                </Badge>
                <h1 className="text-3xl font-bold  sm:text-4xl md:text-5xl lg:text-6xl text-white mb-6 font-cooper">
                  Everything your practice needs in one platform
                </h1>
                <p className="text-white/90 md:text-xl mb-8 font-brown">
                  PetsApp combines all the tools you need to streamline client communication, boost efficiency, and grow
                  your veterinary practice.
                </p>
                <Link href="https://petsapp.com/demo">
                  <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full shadow-[0_4px_14px_0_rgba(255,255,255,0.5)] relative overflow-hidden after:content-[''] after:absolute after:h-1/2 after:w-full after:left-0 after:bottom-0 after:bg-gradient-to-t after:from-white/30 after:to-transparent after:rounded-full after:animate-pulse">
                    Book a demo
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* Success Metrics Section */}
          <section className="py-12 bg-primary">
            <div className="container mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {successMetrics.map((item, index) => (
                  <div key={index} className="bg-primary/30 p-6 rounded-lg">
                    <p className="text-4xl font-bold text-accent font-cooper mb-2">{item.metric}</p>
                    <p className="text-white/80 font-brown">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-16 md:py-24 bg-primary">
            <div className="container mx-auto px-4 md:px-6">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-3xl font-bold sm:text-4xl text-white font-cooper mb-4">
                  Powerful Features for Modern Veterinary Practices
                </h2>
                <p className="text-white/90 md:text-lg font-brown">
                  Discover all the tools and features that make PetsApp the complete solution for your practice.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {allFeatures.map((feature, index) => (
                  <Card
                    key={index}
                    className="border-none shadow-lg hover:shadow-xl transition-all bg-white/10 backdrop-blur-sm overflow-hidden flex flex-col"
                  >
                    <div className="aspect-video relative">
                      <a href={feature.imageLink || feature.link} target="_blank" rel="noopener noreferrer">
                        <Image
                          src={feature.image || "/placeholder.svg"}
                          alt={feature.title}
                          width={800}
                          height={450}
                          className="object-cover w-full h-full"
                        />
                      </a>
                    </div>
                    <CardContent className="p-6 flex flex-col flex-grow">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-white/20 p-2 rounded-full">{feature.icon}</div>
                        <h3 className="text-xl font-bold text-white font-cooper">{feature.title}</h3>
                      </div>
                      <p className="text-white/80 mb-4 font-brown">{feature.description}</p>
                      <ul className="space-y-2 mb-6">
                        {feature.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                            <span className="text-white/80 font-brown">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-auto">
                        <Link href="/demo">
                          <Button className="w-full bg-accent text-white hover:bg-accent/90 font-bold rounded-full">
                            Learn more
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonial Section */}
          <section className="py-16 bg-primary">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-3xl mx-auto text-center">
                <p className="text-2xl italic text-white mb-6 font-brown">
                  "PetsApp has completely transformed how we communicate with our clients. Our team saves hours each day,
                  and clients love the convenience. The ROI has been incredible for our practice."
                </p>
                <p className="text-accent font-semibold font-brown">— Dr. Donita McCants, Practice Owner</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-primary">
            <div className="container mx-auto px-4 md:px-6">
              <div className="bg-tertiary rounded-xl p-8 md:p-12 text-white text-center max-w-4xl mx-auto relative overflow-hidden">
                <div className="absolute right-0 bottom-0 opacity-10 translate-x-1/4 translate-y-1/4">
                  <Image
                    src="https://petsapp.com/_next/image?url=%2Fimages%2FHome%2Fyorkie.png&w=3840&q=75"
                    alt="Yorkie illustration"
                    width={300}
                    height={300}
                    className="w-[300px] h-[300px]"
                  />
                </div>
                <div className="relative z-10">
                  <h2 className="text-2xl md:text-3xl font-cooper font-bold mb-4">
                    Ready to transform your veterinary practice?
                  </h2>
                  <p className="text-white/90 mb-6 max-w-2xl mx-auto font-brown">
                    Join the most successful veterinary teams using PetsApp. Schedule a personalized demo today.
                  </p>
                  <div className="flex justify-center">
                    <Link href="https://meetings.hubspot.com/naomi-oikonomou/revenue-growth?uuid=034c1261-944f-4d7d-a42d-78bb96ddf1ab">
                      <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full shadow-[0_4px_14px_0_rgba(255,255,255,0.5)] relative overflow-hidden after:content-[''] after:absolute after:h-1/2 after:w-full after:left-0 after:bottom-0 after:bg-gradient-to-t after:from-white/30 after:to-transparent after:rounded-full after:animate-pulse">
                        Schedule a demo
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const initialReduxState = await useAllPage(ctx.locale as string);

  return {
    props: {
      initialReduxState: { DYNAMIC: initialReduxState.DYNAMIC },
    },
    revalidate: 600, // 10 Minutes Cache
  };
};

